import { createStore } from 'vuex';
import { StoreMutationType } from '@/types/StoreMutationType';
import {
  SelectionType,
  TowerCanvasState,
  TowerContextMenuState,
  TowerObjectConfig,
  ProgramsPanelState,
  EditDisplayOptionsState,
  DisplayOptions,
  CanvasChangeReason,
  EditTowerInfoModalState,
  PublishPolicyModalState,
} from '@/types/tower/Tower';
import { User } from '@/types/User';
import { ProgramTower } from '@/types/tower/v2/Tower';
import { ProgramPolicyItem } from '@/views/tower/v2/objects/ProgramsLibraryPanel';
import { TowerVisualizerState } from '@/views/tower_visualizer/types/tower';
import CanvasUtils from '@/utilities/tower/v2/Canvas';
import { UserNotification } from '@/types/Notifications';
import { fabric } from 'fabric';
import { UserPermission } from '@/types/UserPermissions';

// LocalStorage key for client view state
const CLIENT_VIEW_STATE_KEY = 'clientViewState';

// Helper functions for localStorage persistence
function loadClientViewState(): ClientViewState {
  try {
    const saved = localStorage.getItem(CLIENT_VIEW_STATE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as ClientViewState;
      return {
        ...parsed,
        showClientViewsModal: false, // Always reset modal state
      };
    }
  } catch (error) {
    console.error('Error loading client view state from localStorage:', error);
  }
  
  // Return default state
  return {
    isClientViewEnabled: false,
    showClientViewsModal: false,
    permissions: [UserPermission.OverviewRead, UserPermission.PoliciesRead, UserPermission.DocumentsRead],
  };
}

function saveClientViewState(state: ClientViewState): void {
  try {
    localStorage.setItem(CLIENT_VIEW_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving client view state to localStorage:', error);
  }
}

function clearClientViewState(): void {
  try {
    localStorage.removeItem(CLIENT_VIEW_STATE_KEY);
  } catch (error) {
    console.error('Error clearing client view state from localStorage:', error);
  }
}

export interface AppState {
  hasError: boolean;
  user?: User;
  clientViewState: ClientViewState;
  loginRedirectPath?: string;
  lastActiveDate?: Date;
  flashMessage?: { text: string; color: string };
  towerCanvasState: TowerCanvasState;
  towerVisualizerState: TowerVisualizerState;
  notifications: UserNotification[];
  resizerRegistry: Record<string, ResizerRegistryItem>;
}

// ResizerRegistryItem is used to store the resizing data and functions
// for a desired element. The width and height should be updated by the resizeFunc
export interface ResizerRegistryItem {
  name: string;
  width?: number;
  height?: number;
  selector: string;
  resizeFunc?: (name: string, selector: string) => void;
}

export interface ClientViewState {
  isClientViewEnabled: boolean;
  activeView?: ClientView;
  showClientViewsModal: boolean;
  permissions: UserPermission[];
}

export interface ClientView {
  id: number;
  name: string;
  allowedAccountIds: number[];
}

const mutations = {
  [StoreMutationType.LOGIN](state: AppState, user: User): void {
    state.user = user;
  },

  [StoreMutationType.LOGOUT](state: AppState): void {
    state.user = undefined;
    // Clear client view state on logout
    state.clientViewState = {
      isClientViewEnabled: false,
      showClientViewsModal: false,
      permissions: [UserPermission.OverviewRead, UserPermission.PoliciesRead, UserPermission.DocumentsRead],
    };
    clearClientViewState();
  },

  [StoreMutationType.SET_LOGIN_REDIRECT_PATH](state: AppState, path: string): void {
    state.loginRedirectPath = path;
  },

  [StoreMutationType.SET_LAST_ACTIVE_DATE](state: AppState, date: Date): void {
    state.lastActiveDate = date;
  },

  [StoreMutationType.SET_FLASH_MESSAGE](
    state: AppState,
    message?: { text: string; color?: 'success' | 'error' | string },
  ): void {
    if (!message) {
      state.flashMessage = undefined;
    } else {
      state.flashMessage = {
        text: message.text,
        color: message.color ?? 'success',
      };
    }
  },

  [StoreMutationType.SET_HAS_ERROR](state: AppState, value: boolean): void {
    state.hasError = value;
  },

  [StoreMutationType.SET_TOWER_CANVAS_SELECTION_TYPE](state: AppState, type: SelectionType): void {
    state.towerCanvasState.selection = type;
  },

  [StoreMutationType.SET_GRID_MODE](state: AppState, value: boolean): void {
    state.towerCanvasState.gridModeEnabled = value;
  },

  [StoreMutationType.SET_LEGEND_MODE](state: AppState, value: boolean): void {
    state.towerCanvasState.legendEnabled = value;
  },

  [StoreMutationType.SET_SNAP_TO_MODE](state: AppState, value: boolean): void {
    state.towerCanvasState.snapToModeEnabled = value;
  },

  [StoreMutationType.SET_TOWER_DISPLAY_OPTIONS](state: AppState, value: DisplayOptions): void {
    state.towerCanvasState.displayOptions = value;
  },

  [StoreMutationType.SET_TOWER_CANVAS_ZOOM_LEVEL](state: AppState, value: number): void {
    state.towerCanvasState.zoomLevel = value;
  },

  [StoreMutationType.SET_TOWER_CONTEXT_MENU_STATE](state: AppState, contextMenuState: TowerContextMenuState): void {
    state.towerCanvasState.contextMenuState = { ...contextMenuState };
  },

  [StoreMutationType.SET_TOWER_SELECTED_OBJECT](state: AppState, selectedObject: TowerObjectConfig): void {
    state.towerCanvasState.selectedObject = selectedObject;
  },

  [StoreMutationType.SET_TOWER_CONFIG_CHANGE](state: AppState, selectedObject: TowerObjectConfig): void {
    state.towerCanvasState.objectConfig = selectedObject;
  },

  [StoreMutationType.UPDATE_CANVAS_OBJECTS_ARRAY](state: AppState, canvasObjects: fabric.Object[]): void {
    /*
        For towers created before the LimitValue -> AggregateLimit refactor, the aggregate limit was stored
        as 'limitValue'. This maps it to 'aggregateLimit'.
     */

    canvasObjects.forEach((obj) => {
      if (obj instanceof fabric.PolicyRect || obj instanceof fabric.CustomPolicyRect) {
        const policyRect = obj as fabric.PolicyRect | fabric.CustomPolicyRect;
        const policyData = policyRect.getPolicyData();
        if (!!policyData.limitValue && !policyData.aggregateLimit) {
          policyData.aggregateLimit = policyData.limitValue;
        }
        policyRect.setPolicyData(policyData);
      }
    });

    state.towerCanvasState.canvasObjects = [...canvasObjects];
  },

  [StoreMutationType.EDIT_TOWER_INFO](state: AppState, editTowerInfoModalState: EditTowerInfoModalState): void {
    state.towerCanvasState.editTowerInfoModalState = {
      ...state.towerCanvasState.editTowerInfoModalState,
      ...editTowerInfoModalState,
    };

    // set isEditing to true when the modal is shown
    state.towerCanvasState.isEditing = editTowerInfoModalState.show;
  },

  /**
   * Enable / Disable custom key mappings for the tower canvas.
   * This is to avoid canvas interactions when editing text fields, modals, etc.
   * @param state
   * @param isEditing
   */
  [StoreMutationType.SET_IS_EDITING](state: AppState, isEditing: boolean): void {
    state.towerCanvasState.isEditing = isEditing;
  },

  [StoreMutationType.EDIT_TOWER_DISPLAY_OPTIONS](
    state: AppState,
    editDisplayOptionsState: EditDisplayOptionsState,
  ): void {
    state.towerCanvasState.editDisplayOptionsState = {
      ...editDisplayOptionsState,
    };
  },

  [StoreMutationType.SET_PROGRAMS_PANEL_STATE](state: AppState, programsPanelState: ProgramsPanelState): void {
    state.towerCanvasState.programsPanelState = { ...programsPanelState };
  },

  [StoreMutationType.SET_CURRENT_TOWER_INSTANCE](state: AppState, tower: ProgramTower): void {
    state.towerCanvasState.currentTowerInstance = tower;
  },

  [StoreMutationType.SET_TOWER_VERSIONS](state: AppState, towerVersions: ProgramTower[]): void {
    state.towerCanvasState.towerVersions = [...towerVersions];
  },

  [StoreMutationType.ADD_TOWER_VERSION](state: AppState, tower: ProgramTower): void {
    state.towerCanvasState.towerVersions = [...state.towerCanvasState.towerVersions, tower];
  },

  [StoreMutationType.SET_PROGRAM_ID](state: AppState, programID: number): void {
    state.towerCanvasState.programId = programID;
  },

  [StoreMutationType.SET_ACCOUNT_ID](state: AppState, accountID: number): void {
    state.towerCanvasState.accountId = accountID;
  },

  [StoreMutationType.SET_PROGRAM_NAME](state: AppState, programName: string): void {
    state.towerCanvasState.programName = programName;
  },

  [StoreMutationType.SET_ACCOUNT_NAME](state: AppState, accountName: string): void {
    state.towerCanvasState.accountName = accountName;
  },

  [StoreMutationType.SET_PROGRAM_POLICIES](state: AppState, policies: ProgramPolicyItem[]): void {
    state.towerCanvasState.programPolicies = [...policies];
  },

  [StoreMutationType.SET_CUSTOM_POLICIES](state: AppState, customPolicies: ProgramPolicyItem[]): void {
    state.towerCanvasState.customPolicies = [...customPolicies];
  },

  [StoreMutationType.SET_RENEWAL_PROCESSED](state: AppState, renewalProcessed: boolean): void {
    state.towerCanvasState.renewalProcessed = renewalProcessed;
  },

  [StoreMutationType.SET_DELETED_PROGRAM_MODE](state: AppState, value: boolean): void {
    state.towerCanvasState.isDeletedProgramMode = value;
  },

  [StoreMutationType.DUPLICATE_FABRIC_OBJECT](state: AppState, value: boolean): void {
    state.towerCanvasState.duplicateFabricObject = value;
  },

  [StoreMutationType.DELETE_FABRIC_OBJECT](state: AppState, value: boolean): void {
    state.towerCanvasState.deleteFabricObject = value;
  },

  [StoreMutationType.SET_EDIT_MODE](state: AppState, value: boolean): void {
    state.towerCanvasState.editMode = value;
  },

  [StoreMutationType.SET_LOAD_ERROR](state: AppState, value: boolean): void {
    state.towerCanvasState.loadError = value;
  },

  [StoreMutationType.ACCEPT_LEGAL_DISCLAIMER](state: AppState): void {
    if (state.user) {
      state.user.legalDisclaimerAccepted = true;
    }
  },

  [StoreMutationType.SET_CANVAS_CHANGE_REASON](state: AppState, value: CanvasChangeReason): void {
    state.towerCanvasState.canvasChangeReason = value;
  },

  [StoreMutationType.ADD_TOWER_HISTORY](state: AppState, tower: ProgramTower): void {
    const stack = state.towerCanvasState.towerHistoryStack;

    // add very first tower to the stack
    if (state.towerCanvasState.towerHistoryStack.length === 0) {
      state.towerCanvasState.towerHistoryStack = [{ ...tower }];
      state.towerCanvasState.currentTowerHistoryIndex = 0;
      return;
    }

    // if the index is not set correctly, set it to the latest tower in history
    if (state.towerCanvasState.currentTowerHistoryIndex === undefined) {
      console.error('currentTowerHistoryIndex is undefined, fixing it to point to the latest tower in history');
      state.towerCanvasState.towerHistoryStack = [...state.towerCanvasState.towerHistoryStack, { ...tower }];
      state.towerCanvasState.currentTowerHistoryIndex = state.towerCanvasState.towerHistoryStack.length - 1;
      return;
    }

    if (state.towerCanvasState.currentTowerHistoryIndex < state.towerCanvasState.towerHistoryStack.length - 1) {
      state.towerCanvasState.towerHistoryStack = state.towerCanvasState.towerHistoryStack.slice(
        0,
        state.towerCanvasState.currentTowerHistoryIndex + 1,
      );
      state.towerCanvasState.towerHistoryStack = [...state.towerCanvasState.towerHistoryStack, { ...tower }];
      state.towerCanvasState.currentTowerHistoryIndex = state.towerCanvasState.towerHistoryStack.length - 1;
      return;
    }

    state.towerCanvasState.towerHistoryStack = [...stack, { ...tower }];
    state.towerCanvasState.currentTowerHistoryIndex = state.towerCanvasState.towerHistoryStack.length - 1;
  },
  [StoreMutationType.UNDO_TOWER_HISTORY](state: AppState): void {
    if (state.towerCanvasState.currentTowerHistoryIndex) {
      state.towerCanvasState.currentTowerHistoryIndex--;
    }
  },
  [StoreMutationType.REDO_TOWER_HISTORY](state: AppState): void {
    if (state.towerCanvasState.currentTowerHistoryIndex == undefined) {
      return;
    }

    if (state.towerCanvasState.towerHistoryStack.length === 0) {
      return;
    }

    if (state.towerCanvasState.currentTowerHistoryIndex >= state.towerCanvasState.towerHistoryStack.length - 1) {
      return;
    }

    state.towerCanvasState.currentTowerHistoryIndex++;
  },
  [StoreMutationType.RESET_TOWER_HISTORY](state: AppState): void {
    const currentIndex = state.towerCanvasState.currentTowerHistoryIndex;
    const towerHistoryStack = state.towerCanvasState.towerHistoryStack;

    if (currentIndex == undefined) {
      return;
    }

    if (towerHistoryStack.length === 0) {
      return;
    }

    if (currentIndex > towerHistoryStack.length - 1) {
      return;
    }

    state.towerCanvasState.towerHistoryStack = [];
    state.towerCanvasState.currentTowerHistoryIndex = undefined;
  },
  [StoreMutationType.SET_ACTIVE_CANVAS_OBJECT](state: AppState, obj?: fabric.Object | null): void {
    state.towerCanvasState.activeCanvasObject = obj ? obj : undefined;
  },

  [StoreMutationType.SHOW_TOWER_EXPORT_OPTIONS](state: AppState, value: boolean): void {
    state.towerCanvasState.showExportOptions = value;
  },

  [StoreMutationType.TOWER_EXPORT_FILE_TYPE](state: AppState, value: string): void {
    state.towerCanvasState.exportType = value;
  },

  [StoreMutationType.SHOW_VISUALIZER_EXPORT_OPTIONS](state: AppState, value: boolean): void {
    state.towerVisualizerState.showExportOptions = value;
  },
  [StoreMutationType.SET_TOWER_EDITOR_VISIBLE](state: AppState, value: boolean): void {
    state.towerCanvasState.isTowerEditorVisible = value;
  },
  [StoreMutationType.SET_TOWER_EDITOR_EXPANDED](state: AppState, value: boolean): void {
    state.towerCanvasState.isTowerEditorExpanded = value;
  },
  [StoreMutationType.EDIT_PUBLISH_TOWER_MODAL_STATE](state: AppState, value: PublishPolicyModalState): void {
    state.towerCanvasState.publishPolicyModalState = { ...state.towerCanvasState.publishPolicyModalState, ...value };
    // set isEditing to true when the modal is shown
    state.towerCanvasState.isEditing = value.show;
  },
  [StoreMutationType.SET_LIMIT_PREMIUM_COUNTER_MODE](state: AppState, value: boolean): void {
    state.towerCanvasState.limitPremiumCounterEnabled = value;
  },

  [StoreMutationType.ENABLE_CLIENT_VIEW_MODE](state: AppState, clientView: ClientView): void {
    state.clientViewState.isClientViewEnabled = true;
    state.clientViewState.activeView = clientView;
    if (state.user) {
      state.user.legalDisclaimerAccepted = true;
    }
    saveClientViewState(state.clientViewState);
  },
  [StoreMutationType.DISABLE_CLIENT_VIEW_MODE](state: AppState): void {
    state.clientViewState.isClientViewEnabled = false;
    state.clientViewState.activeView = undefined;
    clearClientViewState();
  },
  [StoreMutationType.SET_SHOW_CLIENT_VIEWS_MODAL](state: AppState, show: boolean): void {
    state.clientViewState.showClientViewsModal = show;
  },

  [StoreMutationType.SET_NOTIFICATIONS](state: AppState, notifications: UserNotification[]): void {
    state.notifications = notifications;
  },

  [StoreMutationType.ADD_RESIZE_REGISTRY_ITEM](state: AppState, registryItem: ResizerRegistryItem): void {
    state.resizerRegistry = { ...state.resizerRegistry, ...{ [registryItem.name]: registryItem } };
  },
  [StoreMutationType.UPDATE_RESIZE_REGISTRY_ITEM](
    state: AppState,
    payload: { name: string; width?: number; height?: number },
  ): void {
    if (!payload) {
      console.error('No payload provided to UPDATE_RESIZE_REGISTRY_ITEM mutation');
    }
    const item = state.resizerRegistry[payload.name];
    if (item) {
      item.width = payload.width;
      item.height = payload.height;
      state.resizerRegistry = { ...state.resizerRegistry };
    }
  },
  [StoreMutationType.REMOVE_RESIZE_REGISTRY_ITEM](state: AppState, registryItemName: string): void {
    const registry = { ...state.resizerRegistry };
    delete registry[registryItemName];
    state.resizerRegistry = { ...registry };
  },
  [StoreMutationType.RESET_CLIENT_VIEW_PERMISSIONS](state: AppState): void {
    state.clientViewState.permissions = [
      UserPermission.OverviewRead,
      UserPermission.PoliciesRead,
      UserPermission.DocumentsRead,
    ];
    saveClientViewState(state.clientViewState);
  },
  [StoreMutationType.ADD_CLIENT_VIEW_PERMISSION](state: AppState, permission: UserPermission): void {
    state.clientViewState.permissions = [...state.clientViewState.permissions, permission];
    saveClientViewState(state.clientViewState);
  },
  [StoreMutationType.REMOVE_CLIENT_VIEW_PERMISSION](state: AppState, permission: UserPermission): void {
    state.clientViewState.permissions = state.clientViewState.permissions.filter((p) => {
      return p !== permission;
    });
    saveClientViewState(state.clientViewState);
  },
};

export const Store = createStore<AppState>({
  state: {
    hasError: false,
    user: undefined,
    loginRedirectPath: undefined,
    flashMessage: undefined,
    towerCanvasState: {
      isTowerEditorVisible: false,
      isTowerEditorExpanded: true,
      currentTowerInstance: undefined,
      selection: 'selection',
      selectedObject: undefined,
      gridModeEnabled: false,
      legendEnabled: false,
      snapToModeEnabled: true,
      limitPremiumCounterEnabled: true,
      gridSquareSize: 25,
      displayOptions: {
        carrier: { enabled: true, customName: '' },
        aggregateLimit: { enabled: false, customName: '' },
        occurrenceLimit: { enabled: false, customName: '' },
        premium: { enabled: true, customName: '' },
        aggregate: { enabled: false, customName: '' },
        occurrence: { enabled: false, customName: '' },
        amBest: { enabled: false, customName: '' },
        coverage: { enabled: true, customName: '' },
        quotaSharePercent: { enabled: false, customName: '' },
      },
      contextMenuState: {
        show: false,
        left: '0',
        top: '0',
        activeObjectId: undefined,
        objectType: undefined,
      },
      scrollX: 0,
      scrollY: 0,
      canvasObjects: [],
      editDisplayOptionsState: {
        show: false,
      },
      editTowerInfoModalState: {
        show: false,
        towerId: undefined,
      },
      publishPolicyModalState: {
        show: false,
        policy: undefined,
        policyType: undefined,
      },
      programsPanelState: {
        show: false,
        tab: 'program',
      },
      zoomLevel: CanvasUtils.DEFAULT_ZOOM,
      programId: undefined,
      programName: undefined,
      accountName: undefined,
      towerVersions: [],
      duplicateFabricObject: false,
      deleteFabricObject: false,
      editMode: true,
      loadError: false,
      canvasChangeReason: undefined,
      towerHistoryStack: [],
      currentTowerHistoryIndex: undefined,
      activeCanvasObject: undefined,
      exportType: '',
      showExportOptions: false,
      isEditing: false,
      customPolicies: [],
      renewalProcessed: false,
      isDeletedProgramMode: false,
    },
    towerVisualizerState: {
      showExportOptions: false,
    },
    clientViewState: loadClientViewState(),
    notifications: [],
    resizerRegistry: {} as Record<string, ResizerRegistryItem>,
  },
  mutations,
  actions: {},
  modules: {},
});

export const Commit = <K extends keyof typeof mutations & StoreMutationType>(
  value: K,
  payload?: Parameters<(typeof mutations)[K]>[1],
): void => Store.commit(value, payload);

/**
 * @deprecated Use `IsUserAClient` instead
 */
export function isUserExternalClient(): boolean {
  return Store.state.user?.isExternalClient ?? false;
}

export default Store;
